import { useNavigate } from "react-router-dom";
import { X, Trash2, ShoppingBag, ArrowRight } from "lucide-react";
import { useCart } from "@/contexts/CartContext";

export default function CartDrawer() {
  const { cartItems, removeFromCart, cartSubtotal, cartShipping, cartTotal, isCartOpen, setIsCartOpen } = useCart();
  const navigate = useNavigate();

  const handleCheckout = () => {
    setIsCartOpen(false);
    navigate("/checkout");
  };

  if (!isCartOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/40 z-50 backdrop-blur-sm" onClick={() => setIsCartOpen(false)} />

      {/* Drawer */}
      <div className="fixed right-0 top-0 h-full w-full max-w-md bg-white z-50 shadow-2xl flex flex-col" data-testid="cart-drawer">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-[#E6E2D3]">
          <div className="flex items-center gap-2">
            <ShoppingBag className="w-5 h-5 text-[#2F5233]" />
            <h2 className="text-lg font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>
              Your Cart ({cartItems.length})
            </h2>
          </div>
          <button onClick={() => setIsCartOpen(false)} className="p-2 rounded-full hover:bg-[#E6E2D3] transition-colors" data-testid="close-cart">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Items */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {cartItems.length === 0 ? (
            <div className="text-center py-12">
              <ShoppingBag className="w-12 h-12 text-[#E6E2D3] mx-auto mb-4" />
              <p className="text-[#5A5A5A] font-medium">Your cart is empty</p>
              <button onClick={() => { setIsCartOpen(false); navigate("/shop"); }} className="mt-4 text-[#2F5233] font-bold hover:underline">
                Browse Books
              </button>
            </div>
          ) : (
            cartItems.map(item => (
              <div key={item.product_id} className="flex items-start gap-3 p-4 bg-[#FDFBF7] rounded-2xl border border-[#E6E2D3]" data-testid={`cart-item-${item.product_id}`}>
                <div className="w-10 h-10 bg-[#2F5233] rounded-xl flex items-center justify-center flex-shrink-0">
                  <ShoppingBag className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm text-[#1A1A1A] leading-tight">{item.name}</p>
                  <div className="mt-1 space-y-0.5">
                    <p className="text-xs text-[#5A5A5A]">Price: ${item.price.toFixed(2)}</p>
                    {item.shipping > 0 && <p className="text-xs text-[#5A5A5A]">Shipping: ${item.shipping.toFixed(2)}</p>}
                    {item.shipping === 0 && <p className="text-xs text-[#2F5233] font-medium">Free Shipping</p>}
                  </div>
                  {item.badge && (
                    <span className="inline-block mt-1 text-xs bg-[#D2691E] text-white px-2 py-0.5 rounded-full">{item.badge}</span>
                  )}
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="font-bold text-[#2F5233]">${item.total.toFixed(2)}</p>
                  <button onClick={() => removeFromCart(item.product_id)} className="mt-2 p-1 hover:text-red-500 transition-colors" data-testid={`remove-${item.product_id}`}>
                    <Trash2 className="w-4 h-4 text-[#5A5A5A] hover:text-red-500" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        {cartItems.length > 0 && (
          <div className="p-6 border-t border-[#E6E2D3] space-y-3">
            <div className="space-y-1">
              <div className="flex justify-between text-sm text-[#5A5A5A]">
                <span>Subtotal</span><span>${cartSubtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm text-[#5A5A5A]">
                <span>Shipping</span><span>{cartShipping > 0 ? `$${cartShipping.toFixed(2)}` : "Free"}</span>
              </div>
              <div className="flex justify-between font-bold text-[#1A1A1A] text-lg pt-1 border-t border-[#E6E2D3]">
                <span>Total</span><span data-testid="cart-total">${cartTotal.toFixed(2)}</span>
              </div>
            </div>
            <button
              onClick={handleCheckout}
              className="w-full bg-[#2F5233] text-white py-4 rounded-full font-bold text-lg hover:bg-[#1E3A21] transition-all hover:scale-[1.02] flex items-center justify-center gap-2 animate-pulse-glow"
              data-testid="checkout-button"
            >
              Proceed to Checkout <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        )}
      </div>
    </>
  );
}
